import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { Log, User, UserManager } from 'oidc-client-ts';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  protected userManager: UserManager;

  private router = inject(Router);

  private user = signal<User | null>(null);

  getUser = computed(() => this.user());
  isUserExpired = computed(() => this.user()?.expired ?? true);
  isLoggedIn = computed(() => !this.isUserExpired());

  constructor() {
    this.userManager = new UserManager(environment.authentication.settings);

    this.userManager.events.addUserLoaded(async () => this.user.set(await this.userManager.getUser()));
    this.userManager.events.addUserUnloaded(async () => this.user.set(await this.userManager.getUser()));

    if (environment.debugLogging) {
      Log.setLogger(console);
      Log.setLevel(Log.DEBUG);
    }
  }

  async init(path: string): Promise<void> {
    await this.signinSilent();

    if (path.includes('signin-callback')) {
      await this.userManager.signinCallback(path);
    } else if (path.includes('signout-callback')) {
      await this.userManager.signoutCallback(path);
    }

    this.user.set(await this.userManager.getUser());

    return this.router.navigateByUrl(this.getUser() ? path : 'start').then(() => undefined);
  }

  private async signinSilent(): Promise<void> {
    const user = await this.userManager.getUser();

    if (user?.expired) {
      // access_token expired, try refresh_token first
      try {
        const authenticatedUser = await this.userManager.signinSilent();
        if (!authenticatedUser) {
          await this.userManager.removeUser();
        }
      } catch (error) {
        console.error('Error refreshing token:', error);
        await this.userManager.removeUser();
      }
    }
  }

  async getToken(): Promise<string | undefined> {
    const user = await this.userManager.getUser();

    return user?.access_token;
  }

  login(): Promise<void> {
    return this.userManager.signinRedirect();
  }

  logout(): Promise<void> {
    return this.userManager.signoutRedirect();
  }
}
